(ns common.router
  "Routing utils that might be used by views"
  (:require [assemble.assets-page.view :refer [assets-page]]
            [assemble.templates-page.view :refer [templates-page]]
            [clojure.spec.alpha :as spec]
            [common.db.config :as config]
            [common.google-analytics :as ga]
            [common.queries :refer [query-client]]
            [common.resource.query.adset :as adset.q]
            [common.resource.query.advertiser :as advertiser.q]
            [common.resource.query.agency :as agency.q]
            [common.resource.query.campaign :as campaign.q]
            [common.resource.query.product-store :as product-store.q]
            [common.widgets.error-boundary :refer [error-boundary]]
            [manage.ad-variant-preview.view :as ad-variant-preview]
            [manage.ad-variant.page.view :refer [ad-variant-page]]
            [manage.adset-activation-page.view.page :refer [adset-activation-page]]
            [manage.adset-preview-page.view :refer [shared-adset-preview-page]]
            [manage.adset.view.page-wrapper :refer [adset-page-wrapper]]
            [manage.advertiser-page.view :refer [advertiser-page]]
            [manage.campaign-page.view :refer [campaign-page]]
            [manage.content-configuration-page.view.core :refer [content-configuration-page]]
            [manage.content-variant-page.view.core :refer [content-variant-page]]
            [manage.external-templates-page.view :refer [external-template-page]]
            [manage.feed-page.view :refer [feed-page]]
            [manage.poi.selectors.page :refer [poi-selector-page]]
            [manage.poi.view :refer [poi-page]]
            [manage.product-page.view :refer [products-page]]
            [manage.studio-preview.view :refer [studio-preview-page]]
            [manage.variants-preview-page.view :refer [shared-variants-preview-page]]
            [manage.video-rendering.view :as video-rendering]
            [measure.report-page.view :refer [report-page]]
            [measure.shared-report-download-page.view :refer [shared-report-download-page]]
            [portal.advertiser-configuration-page.view.core :refer [advertiser-configuration-page]]
            [portal.advertiser-management.view :refer [advertiser-management-page]]
            [portal.agency-configuration-page.view :refer [agency-configuration-page]]
            [portal.agency-selection-page.view :refer [agency-selection-page]]
            [portal.delivery-page.view :refer [delivery-page]]
            [portal.forgot-password-page.view :refer [forgot-password-page]]
            [portal.login-page.view :refer [login-page]]
            [portal.password-reset-page.view :refer [password-reset-page]]
            [portal.social-monitoring.view :refer [social-monitoring-page]]
            [portal.terms-and-conditions-page.view :refer [terms-and-conditions-page]]
            [portal.user-account-page.view.core :refer [user-account-page]]
            [portal.user-management-page.view :refer [user-management-page]]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [reitit.coercion.spec :as rss]
            [reitit.frontend :as r.fe]
            [reitit.frontend.easy :as r.easy]
            [spec-tools.data-spec :as ds]
            [superadmin.router.core :as superadmin-router]
            [uix.core :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

;; Spec definitions
(spec/def :page.content-configuration/menu keyword?)
(spec/def :page.content-configuration/entity keyword?)
(spec/def :page.content-configuration/campaign-tags int?)

(spec/def :page.content-configuration/query-params
  (spec/keys :opt-un [:page.content-configuration/menu
                      :page.content-configuration/entity
                      :page.content-configuration/campaign-tags]))

(spec/def :page.ad-variant-preview/share boolean?)
(spec/def :page.ad-variant-preview/review boolean?)

(spec/def :page.ad-variant-preview/query-params
  (spec/keys :opt-un [:page.ad-variant-preview/share
                      :page.ad-variant-preview/review]))

(spec/def :page.ad-variant-creation/tab keyword?)

(spec/def :page.ad-variant-creation/query-params
  (spec/keys :opt-un [:page.ad-variant-creation/tab]))

(defui router-component []
  ;; NOTE: Route-data uses var to refer to components, so the
  ;; fn implementation isn't stored to app-db.
  ;; Var can be used as component.
  (let [view-wrapper (use-subscribe [:router/view-wrapper])
        view-component (use-subscribe [:router/view-component])
        path-params (use-subscribe [:router/path-params])]
    ($ error-boundary
       ($ view-wrapper
          (cond
            (nil? view-component)
            nil

            (.-uix-component? ^js view-component)
            ($ view-component path-params)

            (fn? view-component)
            (r/as-element [:f> view-component path-params]))))))

(def unified-routes
  ["" {:controllers [{:parameters {:path [:agency-id :advertiser-id :campaign-id :adset-id
                                          :token :preview-token :preview-adset-id
                                          :content-function-version :share-link-id]
                                   :query [:entity :entity-id :product-selector-id
                                           :feed-id :advertiser-id :cursor]}
                      :start (fn [{:keys [path query]}]
                               (rf/dispatch [:context/set path query]))}
                     {:identity :data
                      :start (fn [{:keys [name sub-title]}]
                               (ga/track-virtual-page! {:pageUrl (.-href js/window.location)
                                                        :pageTitle (if (some? sub-title)
                                                                     (str sub-title " | Creative Optimizations")
                                                                     "Creative Optimizations")})
                               (rf/dispatch [:sentry/tag-page-id name]))}]
       ;; Active tab
       :parameters {:fragment (ds/maybe keyword?)}}

   ;; ===== Public & Auth Routes (no prefix) =====
   ["/login" {:name :common/login
              :scope :user-login
              :sub-title "Login"
              :view #'login-page}]

   ["/terms-and-conditions" {:name :common/tac
                             :scope :public
                             :parameters {:query {(ds/opt :return) keyword?}}
                             :view #'terms-and-conditions-page}]

   ["/password-reset"
    ["" {:name :common/forgot-password
         :view #'forgot-password-page
         :scope :user-login
         :sub-title "Password Reset"}]
    ["/:token" {:parameters {:path {:token string?}}
                :name :common/password-reset
                :view #'password-reset-page
                :scope :user-login
                :sub-title "Password Reset"}]]

   ["/my-account" {:name :common/user
                   :view #'user-account-page
                   :scope :user
                   :sub-title "My Account"}]

   ["/agencies" {:name :common/agency-selection
                 :scope :user
                 :view #'agency-selection-page
                 :sub-title "Select Project"}]

   ;; Shared/public preview routes
   ["/adset-preview/:preview-token" {:parameters {:path {:preview-token string?}}
                                     :controllers [{:identity :data
                                                    :start #(rf/dispatch [:resource.auth/init])}]
                                     :scope :public
                                     :name :common/shared-rules-preview
                                     :view #'shared-adset-preview-page}]

   ["/variants-preview/:preview-token" {:parameters {:path {:preview-token string?}
                                                     :query {:feed-id int?
                                                             :advertiser-id int?
                                                             :cursor string?}}
                                        :controllers [{:identity :data
                                                       :start #(rf/dispatch [:resource.auth/init])}]
                                        :scope :public
                                        :name :common/shared-content-variants-preview
                                        :view #'shared-variants-preview-page}]

   ["/preview-external-template/:preview-adset-id" {:parameters {:path {:preview-adset-id int?}}
                                                    :scope :public
                                                    :name :common/external-template-preview
                                                    :view #'external-template-page}]

   ["/studio-preview/:adset-id" {:parameters {:path {:adset-id int?}}
                                 :name :common/studio-preview
                                 :scope :user
                                 :view #'studio-preview-page}]

   ["/ad-variants-preview/:share-link-id" {:parameters {:path {:share-link-id string?}
                                                        :query :page.ad-variant-preview/query-params}
                                           :name :common/shared-ad-variant-preview
                                           :scope :public
                                           :view #'ad-variant-preview/shared-ad-variant-preview-page
                                           :controllers [{:parameters {:path [:share-link-id]}
                                                          :start #(rf/dispatch [:manage.ad-variant-preview.db/init-page])}]}]

   ["/report/download/:share-link-id" {:parameters {:path {:share-link-id string?}}
                                       :scope :public
                                       :name :common/external-report-download
                                       :view #'shared-report-download-page}]

   ;; ===== Agency Level Routes =====
   ["/:agency-id" {:parameters {:path {:agency-id int?}}
                   :scope :user
                   :breadcrumbs [{:entity :agency}]
                   :controllers [agency.q/ensure-agency-controller]}

    ;; Agency-level pages
    ["/configuration" {:breadcrumbs [{:entity :adservers :link :agency/configuration :label "Adservers"}]
                       :name :agency/configuration
                       :view #'agency-configuration-page
                       :sub-title "Agency Configuration"
                       :permissions ["adservers.edit"]}]

    ["/user-management" {:breadcrumbs [{:entity :users :link :agency/user-management :label "User & role management"}]
                         :name :agency/user-management
                         :view #'user-management-page
                         :sub-title "User management"
                         :permissions ["useraccounts.view"]}]

    ["/delivery" {:breadcrumbs [{:entity :delivery :link :agency/delivery :label "Delivery insights"}]
                  :name :agency/delivery
                  :view #'delivery-page
                  :sub-title "Delivery"
                  :permissions ["advertisers.create" "advertisers.edit" "advertisers.delete"]
                  :controllers [{:start #(rf/dispatch [:table.delivery/init])}]}]

    ;; ===== Advertiser Level Routes =====
    ["/advertiser-management" {:breadcrumbs [{:entity :advertiser-management
                                              :link :agency/advertiser-management
                                              :label "Advertiser management"}]
                               :name :agency/advertiser-management
                               :view #'advertiser-management-page
                               :sub-title "Advertiser management"
                               :controllers [{:start #(rf/dispatch [:table.advertiser/init])}]}]

    ["/advertiser/:advertiser-id" {:parameters {:path {:advertiser-id int?}}
                                   :breadcrumbs [{:entity :advertiser :link :advertiser/page}]
                                   :controllers [advertiser.q/ensure-advertiser-controller]}

     ;; Advertiser home
     ["" {:name :advertiser/page
          :sub-title "Home"
          :view #'advertiser-page}]

     ["/configuration" {:breadcrumbs [{:entity :advertiser-management
                                       :link :advertiser/configuration
                                       :label "Advertiser configuration"}]
                        :name :advertiser/configuration
                        :view #'advertiser-configuration-page
                        :sub-title "Advertiser configuration"
                        :permissions ["advertisers.edit"]
                        :controllers [{:parameters {:path [:advertiser-id]}
                                       :start #(rf/dispatch [:page.advertiser-configuration/init])
                                       :stop #(rf/dispatch [:page.advertiser-configuration/destroy])}]}]

     ["/social-monitoring" {:breadcrumbs [{:entity :social-monitoring
                                           :link :advertiser/social-monitoring
                                           :label "Social monitoring"}]
                            :name :advertiser/social-monitoring
                            :view #'social-monitoring-page
                            :sub-title "Social monitoring"}]

     ;; Feeds
     ["/feeds" {:breadcrumbs [{:entity :feeds :link :advertiser/feeds :label "Dynamic feeds"}]
                :name :advertiser/feeds
                :permissions ["adsets2.view"]
                :sub-title "Dynamic feeds"
                :controllers [{:parameters {:path [:advertiser-id]}
                               :start #(rf/dispatch [:table.feed/init-table-with-type :lookup])}]
                :view #'feed-page}]

     ;; Products
     ["/products" {:breadcrumbs [{:entity :products :link :advertiser/products :label "Products"}]
                   :sub-title "Products"
                   :permissions ["products.view"]
                   :controllers [{:parameters {:path [:advertiser-id]}
                                  :start (fn [{path-params :path}]
                                           (.ensureQueryData query-client (product-store.q/product-store-query path-params)))}]
                   :view #'products-page}
      ["" {:name :advertiser/products}]
      ["/selector" {:name :advertiser/product-selector
                    :parameters {:query {(ds/opt :product-selector-id) any?}}}]]

     ;; POI
     ["/poi" {:breadcrumbs [{:entity :poi :link :advertiser/poi :label "POI"}]
              :sub-title "POI"
              :view #'poi-page}
      ["" {:name :advertiser/poi}]
      ["/selector" {:name :advertiser/poi-selector
                    :view #'poi-selector-page
                    :parameters {:query {(ds/opt :selector-id) any?}}}]]

     ;; Assets
     ["/assets" {:breadcrumbs [{:entity :assets :link :advertiser/assets :label "Media"}]
                 :parameters {:query {(ds/opt :node) int?
                                      (ds/opt :folder) int?
                                      (ds/opt :file) uuid?}}
                 :name :advertiser/assets
                 :view assets-page
                 :sub-title "Media"
                 :asset-tree/default-node-folder-type "assets"
                 :controllers [{:parameters {:path [:advertiser-id]}
                                :start #(rf/dispatch [:assemble.resource.asset-tree/list-folders])}]}]

     ;; Brand kit
     ["/brand-kit" {:breadcrumbs [{:entity :brand-kit :link :advertiser/brand-kit :label "Brand guidelines"}]
                    :parameters {:query {(ds/opt :node) int?
                                         (ds/opt :folder) int?
                                         (ds/opt :file) uuid?
                                         (ds/opt :font-family) string?}}
                    :name :advertiser/brand-kit
                    :view assets-page
                    :sub-title "Brand guidelines"
                    :asset-tree/default-node-folder-type "fonts"
                    :controllers [{:parameters {:path [:advertiser-id]}
                                   :start #(rf/dispatch [:assemble.resource.asset-tree/list-folders])}]}]

     ;; Templates
     ["/templates" {:breadcrumbs [{:entity :templates :link :advertiser/templates :label "Templates"}]
                    :parameters {:query {(ds/opt :node) int?
                                         (ds/opt :folder) int?
                                         (ds/opt :file) int?}}
                    :name :advertiser/templates
                    :sub-title "Templates"
                    :view templates-page
                    :asset-tree/default-node-folder-type "templates"
                    :controllers [{:parameters {:path [:advertiser-id]}
                                   :start #(rf/dispatch [:assemble.resource.asset-tree/list-folders])}]}]

     ;; Reports
     ["/reports" {:breadcrumbs [{:entity :reports :link :advertiser/reports :label "Reports"}]
                  :parameters {:query {(ds/opt :new-report) any?
                                       (ds/opt :adset-id) int?
                                       (ds/opt :channel) string?}}
                  :controllers [{:parameters {:path [:advertiser-id]}
                                 :start (fn [{path-params :path}]
                                          (.prefetchQuery query-client (adset.q/adsets-query path-params)))}]
                  :name :advertiser/reports
                  :sub-title "Reports"
                  :view #'report-page}]

     ;; Video Rendering
     ["/video-rendering" {:breadcrumbs [{:entity :video-rendering
                                         :link :advertiser/video-rendering
                                         :label "Video Rendering"}]
                          :name :advertiser/video-rendering
                          :sub-title "Video Rendering"
                          :view #'video-rendering/video-rendering-root
                          :permissions ["advertisers.view"]
                          :controllers [{:parameters {:path [:advertiser-id]}}]}]

     ;; ===== Campaign Level Routes =====
     ["/campaign/:campaign-id" {:parameters {:path {:campaign-id int?}
                                             :query {(ds/opt :campaign-tags) int?}}
                                :breadcrumbs [{:entity :campaign :link :campaign/page}]
                                :controllers [{:parameters {:path [:campaign-id]}
                                               :start (fn [{path-params :path}]
                                                        (.prefetchQuery query-client (campaign.q/campaign-query path-params)))}]}
      ["" {:name :campaign/page
           :sub-title "Home"
           :view #'campaign-page}]

      ;; ===== Adset Level Routes =====
      ["/adset/:adset-id" {:parameters {:path {:adset-id int?}}
                           :breadcrumbs [{:entity :adset}]
                           :controllers [adset.q/ensure-adset-controller]
                           :view-wrapper adset-page-wrapper}

       ;; Content configuration
       ["/content/:content-function-version" {:parameters {:path {:content-function-version string?}
                                                           :query :page.content-configuration/query-params}
                                              :controllers [{:parameters {:path [:adset-id]}
                                                             :start #(rf/dispatch [:page.content-configuration/init])
                                                             :stop #(rf/dispatch [:page.content-configuration/destroy])}]
                                              :view #'content-configuration-page
                                              :sub-title "Content"}
        ["" {:name :adset/content-configuration}]
        ["/preview/:preview-token" {:name :adset/rules-preview
                                    :parameters {:path {:preview-token string?}}}]]

       ;; Ad variants
       ["/ad-variants" {:parameters {:query {(ds/opt :variant) uuid?
                                             (ds/opt :view-mode) string?
                                             (ds/opt :ad-variants-preview) boolean?
                                             (ds/opt :review) boolean?
                                             (ds/opt :share) boolean?
                                             (ds/opt :campaign-tags) int?
                                             (ds/opt :ad-variant-filter) string?
                                             (ds/opt :show-removed) boolean?}}
                        :view #'ad-variant-page
                        :sub-title "Ad variants"}
        ["" {:name :adset/ad-variants}]
        ["/creation" {:name :adset/ad-variants-creation
                      :parameters {:query :page.ad-variant-creation/query-params}}]]

       ;; Content variants
       ["/content-variants/:content-function-version" {:parameters {:path {:content-function-version string?}
                                                                    :query :page.content-configuration/query-params}
                                                       :controllers [{:parameters {:path [:adset-id]}
                                                                      :start #(rf/dispatch [:page.content-variant/start])
                                                                      :stop #(rf/dispatch [:page.content-variant/destroy])}]
                                                       :view #'content-variant-page
                                                       :sub-title "Content"}
        ["" {:name :adset/content-variants}]
        ["/preview/:preview-token" {:name :adset/content-variants-preview
                                    :parameters {:path {:preview-token string?}
                                                 :query {:feed-id int?
                                                         :cursor string?}}}]]

       ;; TODO: Remove when no longer needed
       ["/activation" {:name :adset/activation
                       :sub-title "Activation"
                       :view #'adset-activation-page}]]]]]

   ;; ===== Superadmin (kept separate) =====
   superadmin-router/routes])

;; Legacy redirect routes (old prefixed paths)
(def redirect-routes
  ["" {}
   ;; Portal redirects
   ["/portal" {}
    ["/login" {:redirect-to :common/login}]
    ["/terms-and-conditions" {:redirect-to :common/tac}]
    ["/password-reset" {:redirect-to :common/forgot-password}]
    ["/password-reset/:token" {:parameters {:path {:token string?}}
                               :redirect-to :common/password-reset}]
    ["/my-account" {:redirect-to :common/user}]
    ["/agencies" {:redirect-to :common/agency-selection}]

    ["/:agency-id" {:parameters {:path {:agency-id int?}}}
     ["/configuration" {:redirect-to :agency/configuration}]
     ["/user-management" {:redirect-to :agency/user-management}]
     ["/delivery" {:redirect-to :agency/delivery}]
     ["/advertiser-management" {:redirect-to :agency/advertiser-management}]
     ["/advertiser-management/:advertiser-id/configuration"
      {:parameters {:path {:advertiser-id int?}}
       :redirect-to :advertiser/configuration}]
     ["/advertiser/:advertiser-id/social-monitoring"
      {:parameters {:path {:advertiser-id int?}}
       :redirect-to :advertiser/social-monitoring}]]]

   ;; Manage redirects
   ["/manage" {}
    ["/adset-preview/:preview-token" {:parameters {:path {:preview-token string?}}
                                      :redirect-to :common/shared-rules-preview}]
    ["/variants-preview/:preview-token" {:parameters {:path {:preview-token string?}}
                                         :redirect-to :common/shared-content-variants-preview}]
    ["/preview-external-template/:preview-adset-id" {:parameters {:path {:preview-adset-id int?}}
                                                     :redirect-to :common/external-template-preview}]
    ["/studio-preview/:adset-id" {:parameters {:path {:adset-id int?}}
                                  :redirect-to :common/studio-preview}]
    ["/ad-variants-preview/:share-link-id" {:parameters {:path {:share-link-id string?}}
                                            :redirect-to :common/shared-ad-variant-preview}]

    ["/:agency-id/advertiser/:advertiser-id" {:parameters {:path {:agency-id int? :advertiser-id int?}}}
     ["" {:redirect-to :advertiser/page}]
     ["/feeds" {:redirect-to :advertiser/feeds}]
     ["/products" {:redirect-to :advertiser/products}]

     ["/campaign/:campaign-id" {:parameters {:path {:campaign-id int?}}}
      ["" {:redirect-to :campaign/page}]

      ["/adset/:adset-id" {:parameters {:path {:adset-id int?}}}
       ["/content/:content-function-version" {:parameters {:path {:content-function-version string?}}
                                              :redirect-to :adset/content-configuration}]
       ["/ad-variants" {:redirect-to :adset/ad-variants}]
       ["/ad-variants/creation" {:redirect-to :adset/ad-variants-creation}]
       ["/content-variants/:content-function-version" {:parameters {:path {:content-function-version string?}}
                                                       :redirect-to :adset/content-variants}]
       ["/activation" {:redirect-to :adset/activation}]]]]]

   ;; Assemble redirects
   ["/assemble" {}
    ["/:agency-id/advertiser/:advertiser-id" {:parameters {:path {:agency-id int? :advertiser-id int?}}}
     ["/assets" {:redirect-to :advertiser/assets}]
     ["/brand-kit" {:redirect-to :advertiser/brand-kit}]
     ["/templates" {:redirect-to :advertiser/templates}]]]

   ;; Measure redirects
   ["/measure" {}
    ["/report/download/:share-link-id" {:parameters {:path {:share-link-id string?}}
                                        :redirect-to :common/external-report-download}]
    ["/:agency-id/advertiser/:advertiser-id" {:parameters {:path {:agency-id int? :advertiser-id int?}}}
     ["" {:redirect-to :advertiser/reports}]]]])

;; Combined routes: unified routes + redirect routes
(def routes
  [unified-routes
   redirect-routes])

(rf/reg-event-fx ::navigate-later
  (fn [_ [_ event]]
    {:fx [[:dispatch-later {:ms 800 :dispatch event}]]}))

(defn on-navigate [new-match history]
  (let [scope (get-in new-match [:data :scope])
        redirect-to (get-in new-match [:data :redirect-to])
        env-ready? (config/config-available?)
        event (cond
                (nil? new-match) [:router/navigate-up history]
                (= :user-login scope) [:router/navigate-user-login-scope new-match]
                (= :user scope) [:router/navigate-user-scope
                                 new-match
                                 [[:cqrs/send :system/actions]
                                  [:cqrs/send :system/available-actions]]]
                (= :public scope) [:router/navigate-public-scope new-match]
                redirect-to [:router/redirect new-match])]
    (if env-ready?
      (rf/dispatch event)
      (rf/dispatch [::navigate-later event]))))

(def router
  (r.fe/router
   routes
   {:data {:coercion rss/coercion}
    :conflicts nil}))

(defn init-routes! []
  (r.easy/start!
   router
   on-navigate
   {:use-fragment false}))
